// userRatingApi.js
import axios from 'axios';

// Base URL for your Spring Boot backend - hardcoded
const BASE_URL = 'https://api.eilandigital.com/eilanApplication-0.0.1-SNAPSHOT/api/user-rating';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000, // Increased timeout to handle slower requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Cancel token storage for managing concurrent requests
const cancelTokens = {};

// Helper function to cancel previous request for a specific key
const cancelPreviousRequest = (key) => {
  if (cancelTokens[key]) {
    cancelTokens[key].cancel('Request cancelled due to new request');
    delete cancelTokens[key];
  }
};

// Add request interceptor for authentication if needed
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if you have one
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't log cancelled requests
    if (axios.isCancel(error)) {
      console.log('Request cancelled:', error.message);
      return Promise.reject({ cancelled: true, message: error.message });
    }
    
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('authToken');
      // Uncomment if you have a login page: window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const userRatingApi = {
  // Get all users rating summary with cancellation support
  getAllUsersRatingSummary: async (viewMode = 'all', period = null, year = null) => {
    const requestKey = 'getAllUsersRatingSummary';
    
    try {
      // Cancel any previous request for this endpoint
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      const params = { viewMode };
      if (period) params.period = period;
      if (year) params.year = year;
      
      console.log('Fetching users with params:', params);
      const response = await apiClient.get('/users', { 
        params,
        cancelToken: source.token 
      });
      
      // Clean up cancel token on success
      delete cancelTokens[requestKey];
      
      console.log('API Response:', response.data);
      return response.data;
    } catch (error) {
      // Clean up cancel token on error
      delete cancelTokens[requestKey];
      
      if (error.cancelled) {
        throw error; // Re-throw cancelled errors as-is
      }
      
      console.error('API Error Details:', {
        message: error.message,
        response: error.response,
        status: error.response?.status,
        data: error.response?.data
      });
      throw new Error(`Failed to fetch users rating summary: ${error.response?.data?.message || error.response?.status || error.message}`);
    }
  },
  
  // Get detailed rating information for a specific user with cancellation support
  getUserRatingDetails: async (userId, viewMode = 'all', period = null, year = null) => {
    const requestKey = `getUserRatingDetails_${userId}`;
    
    try {
      // Cancel any previous request for this user
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      const params = { viewMode };
      if (period) params.period = period;
      if (year) params.year = year;
      
      console.log('Fetching user details with params:', { userId, ...params });
      const response = await apiClient.get(`/user/${userId}/details`, { 
        params,
        cancelToken: source.token 
      });
      
      // Clean up cancel token on success
      delete cancelTokens[requestKey];
      
      console.log('User details response:', response.data);
      return response.data;
    } catch (error) {
      // Clean up cancel token on error
      delete cancelTokens[requestKey];
      
      if (error.cancelled) {
        throw error; // Re-throw cancelled errors as-is
      }
      
      console.error('User details error:', error);
      throw new Error(`Failed to fetch user details for user ${userId}: ${error.response?.data?.message || error.message}`);
    }
  },

  // Get user's performance data for charts
  getUserPerformanceData: async (userId, viewMode = 'monthly', year = null) => {
    const requestKey = `getUserPerformanceData_${userId}`;
    
    try {
      // Cancel any previous request
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      const params = { viewMode };
      if (year) params.year = year;
      
      const response = await apiClient.get(`/user/${userId}/performance`, { 
        params,
        cancelToken: source.token 
      });
      
      // Clean up cancel token
      delete cancelTokens[requestKey];
      
      return response.data;
    } catch (error) {
      delete cancelTokens[requestKey];
      
      if (error.cancelled) {
        throw error;
      }
      
      throw new Error(`Failed to fetch performance data for user ${userId}`);
    }
  },

  // Get user's tasks with filtering and pagination
  getUserTasks: async (userId, options = {}) => {
    try {
      const {
        page = 0,
        size = 10,
        clientName = '',
        projectName = '',
        taskName = '',
        performance = '',
        proofreading = '',
        quality = ''
      } = options;

      const params = { page, size };
      if (clientName) params.clientName = clientName;
      if (projectName) params.projectName = projectName;
      if (taskName) params.taskName = taskName;
      if (performance) params.performance = performance;
      if (proofreading) params.proofreading = proofreading;
      if (quality) params.quality = quality;

      const response = await apiClient.get(`/user/${userId}/tasks`, { params });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch tasks for user ${userId}`);
    }
  },

  // Get user's communication data
  getUserCommunicationData: async (userId, period = null, viewMode = 'monthly') => {
    const requestKey = `getUserCommunicationData_${userId}`;
    
    try {
      // Cancel any previous request
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      const params = { viewMode };
      if (period) params.period = period;

      const response = await apiClient.get(`/user/${userId}/communication`, { 
        params,
        cancelToken: source.token 
      });
      
      // Clean up cancel token
      delete cancelTokens[requestKey];
      
      return response.data;
    } catch (error) {
      delete cancelTokens[requestKey];
      
      if (error.cancelled) {
        throw error;
      }
      
      throw new Error(`Failed to fetch communication data for user ${userId}`);
    }
  },

  // Get user's skills data
  getUserSkillsData: async (userId, period = null, viewMode = 'monthly') => {
    const requestKey = `getUserSkillsData_${userId}`;
    
    try {
      // Cancel any previous request
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      const params = { viewMode };
      if (period) params.period = period;

      const response = await apiClient.get(`/user/${userId}/skills`, { 
        params,
        cancelToken: source.token 
      });
      
      // Clean up cancel token
      delete cancelTokens[requestKey];
      
      return response.data;
    } catch (error) {
      delete cancelTokens[requestKey];
      
      if (error.cancelled) {
        throw error;
      }
      
      throw new Error(`Failed to fetch skills data for user ${userId}`);
    }
  },

  // Get filter options for task filtering
  getTaskFilterOptions: async (userId) => {
    try {
      const response = await apiClient.get(`/user/${userId}/filter-options`);
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch filter options for user ${userId}`);
    }
  },

  // Get tasks summary with date range
  getTasksSummaryWithDateRange: async (startDate, endDate, userId) => {
    const requestKey = `getTasksSummaryWithDateRange_${userId}`;
    
    try {
      // Cancel any previous request
      cancelPreviousRequest(requestKey);
      
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokens[requestKey] = source;
      
      console.log('API call with params:', { startDate, endDate, userId });
      
      const response = await axios.get(`${BASE_URL}/tasks-summary-with-date-range`, {
        params: {
          startDate: startDate || 'all',  // Send 'all' if null/undefined
          endDate: endDate || 'all',      // Send 'all' if null/undefined
          userId: userId
        },
        cancelToken: source.token
      });
      
      // Clean up cancel token
      delete cancelTokens[requestKey];
      
      console.log('API response:', response.data);
      return response.data;
    } catch (error) {
      delete cancelTokens[requestKey];
      
      if (axios.isCancel(error)) {
        console.log('Tasks summary request cancelled:', error.message);
        throw { cancelled: true, message: error.message };
      }
      
      console.error('Error fetching tasks summary with date range:', error);
      throw error;
    }
  },
};

export default userRatingApi;